import { LocationResult } from './locationService.ts';
import { getApiUrl } from '../config/apiConfig.ts';

interface NominatimResponseItem {
  lat: string;
  lon: string;
  display_name: string;
}

function removeVietnameseTones(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

export class SearchService {
  private static FALLBACK_RESULTS: LocationResult[] = [
    // Hà Nội & Miền Bắc
    { coords: { lat: 21.0285, lon: 105.8542 }, label: 'Hồ Hoàn Kiếm, Hoàn Kiếm, Hà Nội' },
    { coords: { lat: 21.0580, lon: 105.8139 }, label: 'Hồ Tây, Tây Hồ, Hà Nội' },
    { coords: { lat: 21.0338, lon: 105.8504 }, label: 'Phố Cổ Tạ Hiện, Hoàn Kiếm, Hà Nội' },
    { coords: { lat: 21.0031, lon: 105.8431 }, label: 'Đại học Bách Khoa, Hai Bà Trưng, Hà Nội' },
    { coords: { lat: 21.0368, lon: 105.7905 }, label: 'Cầu Giấy, Cầu Giấy, Hà Nội' },
    { coords: { lat: 20.8449, lon: 106.6881 }, label: 'Nhà Hát Lớn Hải Phòng, Hồng Bàng, Hải Phòng' },
    // Miền Trung & Tây Nguyên
    { coords: { lat: 16.0680, lon: 108.2208 }, label: 'Cầu Rồng, Hải Châu, Đà Nẵng' },
    { coords: { lat: 16.0592, lon: 108.2435 }, label: 'Bãi biển Mỹ Khê, Ngũ Hành Sơn, Đà Nẵng' },
    { coords: { lat: 15.8801, lon: 108.3380 }, label: 'Phố Cổ Hội An, Quảng Nam' },
    { coords: { lat: 16.4637, lon: 107.5909 }, label: 'Đại Nội Huế, Thừa Thiên Huế' },
    { coords: { lat: 11.9404, lon: 108.4377 }, label: 'Chợ Đà Lạt, Lâm Đồng' },
    { coords: { lat: 12.2388, lon: 109.1967 }, label: 'Quảng trường 2/4, Nha Trang, Khánh Hòa' },
    // TP.HCM & Miền Nam
    { coords: { lat: 10.7769, lon: 106.7009 }, label: 'Chợ Bến Thành, Quận 1, TP. Hồ Chí Minh' },
    { coords: { lat: 10.7745, lon: 106.7036 }, label: 'Phố Đi Bộ Nguyễn Huệ, Quận 1, TP. Hồ Chí Minh' },
    { coords: { lat: 10.7936, lon: 106.7218 }, label: 'Landmark 81, Bình Thạnh, TP. Hồ Chí Minh' },
    { coords: { lat: 10.7721, lon: 106.6983 }, label: 'Dinh Độc Lập, Quận 1, TP. Hồ Chí Minh' },
    { coords: { lat: 10.7552, lon: 106.6575 }, label: 'Chợ Lớn (Bình Tây), Quận 6, TP. Hồ Chí Minh' },
    { coords: { lat: 10.8499, lon: 106.7716 }, label: 'Thành phố Thủ Đức, TP. Hồ Chí Minh' },
    { coords: { lat: 10.0452, lon: 105.7469 }, label: 'Bến Ninh Kiều, Ninh Kiều, Cần Thơ' },
    { coords: { lat: 10.3460, lon: 107.0843 }, label: 'Bãi Sau, Vũng Tàu, Bà Rịa - Vũng Tàu' },
  ];

  public static async searchLocations(
    query: string,
    timeoutMs = 6000
  ): Promise<LocationResult[]> {
    const cleanQuery = query.trim();
    if (!cleanQuery) return [];

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    // 1. Thử gọi qua Cloudflare Edge Proxy trước (hỗ trợ CORS + KV Cache cực nhanh)
    try {
      const edgeUrl = getApiUrl(`/api/search?q=${encodeURIComponent(cleanQuery)}`);
      const response = await fetch(edgeUrl, {
        signal: controller.signal,
      });

      if (response.ok) {
        const data = (await response.json()) as NominatimResponseItem[];
        if (Array.isArray(data) && data.length > 0) {
          clearTimeout(timer);
          return data.map((item) => ({
            coords: {
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon),
            },
            label: item.display_name,
          }));
        }
      }
    } catch {
      // Tiếp tục thử trực tiếp
    }

    // 2. Thử gọi trực tiếp Nominatim OSM (cho môi trường node/test hoặc khi worker offline)
    try {
      const directUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        cleanQuery
      )}&countrycodes=vn&limit=5`;

      const response = await fetch(directUrl, {
        signal: controller.signal,
        headers: {
          'Accept-Language': 'vi,en;q=0.9',
        },
      });

      clearTimeout(timer);

      if (response.ok) {
        const data = (await response.json()) as NominatimResponseItem[];
        if (Array.isArray(data) && data.length > 0) {
          return data.map((item) => ({
            coords: {
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon),
            },
            label: item.display_name,
          }));
        }
      }
    } catch {
      // ignore
    } finally {
      clearTimeout(timer);
    }

    // 3. Fallback danh bạ các địa danh thực tế có hỗ trợ gõ không dấu
    return this.getFilteredFallbacks(cleanQuery);
  }

  private static getFilteredFallbacks(query: string): LocationResult[] {
    const rawLower = query.toLowerCase().trim();
    const noToneQuery = removeVietnameseTones(query);

    const matched = this.FALLBACK_RESULTS.filter((r) => {
      const rawLabel = r.label.toLowerCase();
      const noToneLabel = removeVietnameseTones(r.label);
      return (
        rawLabel.includes(rawLower) ||
        noToneLabel.includes(noToneQuery) ||
        noToneQuery.includes(noToneLabel.split(',')[0]!.trim())
      );
    });

    return matched.length > 0 ? matched : this.FALLBACK_RESULTS.slice(0, 5);
  }

  // --- RECENT & POPULAR SEARCHES ---
  private static RECENT_KEY = 'tradaviahe_recent_searches';
  private static cachedPopular: LocationResult[] | null = null;

  public static getRecentSearches(): LocationResult[] {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return [];
      const raw = localStorage.getItem(this.RECENT_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Array<{ coords: { lat: number; lon: number }; label: string }>;
      if (Array.isArray(parsed)) {
        return parsed.slice(0, 6);
      }
    } catch {
      // ignore
    }
    return [];
  }

  public static saveRecentSearch(coords: { lat: number; lon: number }, label: string): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      const current = this.getRecentSearches();
      const cleanLabel = label.trim();
      // Remove duplicate
      const filtered = current.filter(
        (it) =>
          it.label.toLowerCase() !== cleanLabel.toLowerCase() &&
          !(Math.abs(it.coords.lat - coords.lat) < 0.001 && Math.abs(it.coords.lon - coords.lon) < 0.001)
      );
      filtered.unshift({ coords, label: cleanLabel });
      localStorage.setItem(this.RECENT_KEY, JSON.stringify(filtered.slice(0, 6)));
    } catch {
      // ignore
    }
  }

  public static clearRecentSearches(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      localStorage.removeItem(this.RECENT_KEY);
    } catch {
      // ignore
    }
  }

  public static async fetchPopularSearches(): Promise<LocationResult[]> {
    if (this.cachedPopular && this.cachedPopular.length > 0) {
      return this.cachedPopular;
    }

    try {
      const edgeUrl = getApiUrl('/api/search/popular');
      const res = await fetch(edgeUrl);
      if (res.ok) {
        const data = (await res.json()) as Array<{ label: string; lat: number; lon: number; query?: string }>;
        if (Array.isArray(data) && data.length > 0) {
          this.cachedPopular = data.map((it) => ({
            coords: { lat: it.lat, lon: it.lon },
            label: it.label || it.query || 'Vị trí hot',
          }));
          return this.cachedPopular;
        }
      }
    } catch {
      // fallback
    }

    this.cachedPopular = [
      { coords: { lat: 21.0580, lon: 105.8139 }, label: 'Hồ Tây, Tây Hồ, Hà Nội' },
      { coords: { lat: 21.0368, lon: 105.7905 }, label: 'Cầu Giấy, Cầu Giấy, Hà Nội' },
      { coords: { lat: 10.7936, lon: 106.7218 }, label: 'Landmark 81, Bình Thạnh, TP. Hồ Chí Minh' },
      { coords: { lat: 11.9404, lon: 108.4377 }, label: 'Chợ Đà Lạt, Lâm Đồng' },
      { coords: { lat: 10.0452, lon: 105.7469 }, label: 'Bến Ninh Kiều, Cần Thơ' },
      { coords: { lat: 16.0680, lon: 108.2208 }, label: 'Cầu Rồng, Hải Châu, Đà Nẵng' },
      { coords: { lat: 10.7769, lon: 106.7009 }, label: 'Chợ Bến Thành, Quận 1, TP. Hồ Chí Minh' },
      { coords: { lat: 21.0338, lon: 105.8504 }, label: 'Phố Cổ Tạ Hiện, Hoàn Kiếm, Hà Nội' },
    ];
    return this.cachedPopular;
  }
}
